import React, { useState, useMemo, useEffect } from "react";
import { Row, Col, Modal } from "react-bootstrap";
import { formatCurrency, formatSignedCurrency } from '../../../utilities/formatCurrency';
import { getOrderCostLines, normalizeOrderCost } from '../../../lib/orderCost';
import { formatFirebaseDate } from '../../../utilities/format';
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle, Clock, XCircle, Truck,
  Person, Phone, Calendar, CreditCard, Box, GeoAltFill,
  ShieldExclamation, BoxSeam,
} from "react-bootstrap-icons";
import { getMerchantOrderHistory } from '../../../lib/webService/webService';
import { useMerchantAuth } from '../../../utilities/authCheck';
import { useUserContext } from '../../../context/UserContext';
import { OrderItem } from '../../../components/OrderItem';
import { Seo } from "../../../components/Seo";
import { generateNoIndexSEO } from "../../../lib/seo/metaGenerators";

// ─── Module-level CSS ────────────────────────────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("so-styles")) {
  const s = document.createElement("style");
  s.id = "so-styles";
  s.textContent = `
    @keyframes _so_sk { 0%,100%{opacity:1}50%{opacity:.4} }
    ._so_sk { animation: _so_sk 1.8s ease-in-out infinite; background: rgba(60,60,67,0.08); }
    .so-chips { overflow-x: auto; scrollbar-width: none; -ms-overflow-style: none; }
    .so-chips::-webkit-scrollbar { display: none; }
    .so-modal .modal-content { border-radius: 20px !important; border: none !important; overflow: hidden; box-shadow: 0 24px 60px rgba(0,0,0,0.18) !important; }
  `;
  document.head.appendChild(s);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { label: 'All',        value: 'all' },
  { label: 'Placed',     value: 'placed' },
  { label: 'Accepted',   value: 'accepted' },
  { label: 'Processing', value: 'processing' },
  { label: 'Enroute',    value: 'enroute' },
  { label: 'Completed',  value: 'completed' },
  { label: 'Cancelled',  value: 'cancelled' },
];

const STATUS_COLORS = {
  placed:     '#8E8E93',
  accepted:   '#007AFF',
  processing: '#FF9500',
  enroute:    '#5856D6',
  completed:  '#34C759',
  delivered:  '#34C759',
  cancelled:  '#FF3B30',
};

const STATUS_ICONS = {
  completed:  <CheckCircle size={12} />,
  enroute:    <Truck size={12} />,
  processing: <Clock size={12} />,
  accepted:   <CheckCircle size={12} />,
  placed:     <Clock size={12} />,
  cancelled:  <XCircle size={12} />,
};

const PAGE = {
  minHeight: '100vh',
  backgroundColor: '#F2F2F7',
  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif',
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const Skel = ({ w, h, r = 8, style = {} }) => (
  <div className="_so_sk" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

const StatusPill = ({ status }) => {
  const color = STATUS_COLORS[status] ?? '#8E8E93';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 10px 3px 8px', borderRadius: 20,
      backgroundColor: `${color}1A`, color,
      fontSize: '0.72rem', fontWeight: 600,
      textTransform: 'capitalize', letterSpacing: '0.01em',
    }}>
      {STATUS_ICONS[status]}
      {status}
    </span>
  );
};

const SectionLabel = ({ label }) => (
  <div style={{
    fontSize: '0.68rem', fontWeight: 600,
    color: 'rgba(60,60,67,0.42)', textTransform: 'uppercase',
    letterSpacing: '0.07em', marginBottom: 10,
  }}>
    {label}
  </div>
);

const Divider = () => (
  <div style={{ height: 1, backgroundColor: 'rgba(60,60,67,0.08)', margin: '16px 0' }} />
);

const InfoGridItem = ({ icon, label, value }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
      <span style={{ color: '#007AFF', lineHeight: 1 }}>{icon}</span>
      <span style={{ fontSize: '0.66rem', fontWeight: 600, color: 'rgba(60,60,67,0.38)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </span>
    </div>
    <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#1c1c1e' }}>{value}</div>
  </div>
);

const FilterChips = ({ value, onChange }) => (
  <div className="so-chips" style={{ display: 'flex', gap: 8, paddingBottom: 2 }}>
    {STATUS_OPTIONS.map(opt => {
      const active = value === opt.value;
      const color = opt.value === 'all' ? '#007AFF' : (STATUS_COLORS[opt.value] ?? '#007AFF');
      return (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          style={{
            flexShrink: 0, padding: '6px 14px', borderRadius: 20,
            border: active ? 'none' : '1px solid rgba(60,60,67,0.14)',
            backgroundColor: active ? color : 'rgba(255,255,255,0.9)',
            color: active ? '#fff' : 'rgba(60,60,67,0.65)',
            fontSize: '0.8125rem', fontWeight: active ? 600 : 400,
            cursor: 'pointer', transition: 'all 0.18s ease', whiteSpace: 'nowrap',
          }}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

const InfoRow = ({ icon, text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
    <span style={{ color: 'rgba(60,60,67,0.32)', lineHeight: 1, flexShrink: 0 }}>{icon}</span>
    <span style={{ fontSize: '0.8125rem', color: 'rgba(60,60,67,0.65)', lineHeight: 1.4 }}>{text}</span>
  </div>
);

const OrderCard = ({ order, onViewDetails }) => {
  const itemCount = order.cart.reduce((t, i) => t + i.quantity, 0);
  return (
    <div style={{
      backgroundColor: '#fff',
      borderRadius: 16,
      border: '1px solid rgba(60,60,67,0.09)',
      padding: '14px 16px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <StatusPill status={order.status} />
        <span style={{ fontSize: '0.7rem', color: 'rgba(60,60,67,0.38)', fontWeight: 500 }}>
          {formatFirebaseDate(order.createdAt)}
        </span>
      </div>

      <div style={{
        fontSize: '0.78rem', fontWeight: 700, color: '#1c1c1e',
        marginBottom: 10, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums',
      }}>
        #{order.id}
      </div>

      <div style={{ flex: 1 }}>
        <InfoRow icon={<Person size={12} />} text={`${order.firstName} ${order.lastName}`} />
        <InfoRow icon={<Phone size={12} />}  text={order.phone} />
        <InfoRow icon={<Box size={12} />}    text={`${itemCount} item${itemCount !== 1 ? 's' : ''}`} />
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(60,60,67,0.07)',
      }}>
        <span style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#1c1c1e', letterSpacing: '-0.02em' }}>
          {formatCurrency(order.cost.total)}
        </span>
        <button
          onClick={() => onViewDetails(order)}
          style={{
            background: 'none', border: 'none', padding: 0,
            fontSize: '0.8125rem', fontWeight: 600, color: '#007AFF', cursor: 'pointer',
          }}
        >
          Details →
        </button>
      </div>
    </div>
  );
};

const SkeletonGrid = () => (
  <Row className="g-3">
    {Array.from({ length: 9 }).map((_, i) => (
      <Col xs={12} sm={6} lg={4} key={i}>
        <div style={{ backgroundColor: '#fff', borderRadius: 16, border: '1px solid rgba(60,60,67,0.07)', padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <Skel w={80} h={22} r={11} />
            <Skel w={56} h={13} r={6} />
          </div>
          <Skel w={100} h={13} r={6} style={{ marginBottom: 10 }} />
          <Skel w="82%" h={12} r={6} style={{ marginBottom: 7 }} />
          <Skel w="68%" h={12} r={6} style={{ marginBottom: 7 }} />
          <Skel w="54%" h={12} r={6} style={{ marginBottom: 14 }} />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Skel w={65} h={18} r={6} />
            <Skel w={68} h={18} r={9} />
          </div>
        </div>
      </Col>
    ))}
  </Row>
);

// ─── Order Details Modal ──────────────────────────────────────────────────────

function OrderDetailsModal({ show, onHide, order }) {
  if (!order) return null;

  const getCartItemCount = (cart) => cart.reduce((total, item) => total + item.quantity, 0);

  const toAmount = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined || value === "") continue;
      const amount = Number(value);
      if (!Number.isNaN(amount)) return amount;
    }
    return 0;
  };

  const rawTotal = order?.cost?.total ?? order?.total;
  const costInput = {
    ...order?.cost,
    subTotal:    toAmount(order?.cost?.subTotal, order?.cost?.subtotal, order?.subTotal, order?.subtotal),
    deliveryFee: toAmount(order?.cost?.deliveryFee, order?.deliveryFee, order?.cost?.deliveryAmount, order?.deliveryAmount),
    tax:         toAmount(order?.cost?.tax, order?.tax, order?.taxAmount),
    total:       rawTotal == null || rawTotal === "" ? undefined : toAmount(rawTotal),
  };
  const costBreakdown = normalizeOrderCost(costInput);
  const costLines = getOrderCostLines(costInput);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered dialogClassName="so-modal">
      <Modal.Body style={{ padding: 0 }}>

        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid rgba(60,60,67,0.09)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <h3 style={{ fontSize: '1.0625rem', fontWeight: 700, color: '#1c1c1e', margin: '0 0 2px', letterSpacing: '-0.02em' }}>
              Order Details
            </h3>
            <span style={{ fontSize: '0.78rem', color: 'rgba(60,60,67,0.42)', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
              #{order.id}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <StatusPill status={order.status} />
            <button
              onClick={onHide}
              style={{
                width: 28, height: 28, borderRadius: '50%',
                background: 'rgba(60,60,67,0.08)', border: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', fontSize: '1.1rem', color: 'rgba(60,60,67,0.55)',
                lineHeight: 1, flexShrink: 0,
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ padding: '20px 24px 28px', maxHeight: '68vh', overflowY: 'auto' }}>

          {/* Customer */}
          <SectionLabel label="Customer" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 24px', marginBottom: 4 }}>
            <InfoGridItem icon={<Person size={13} />}     label="Name"    value={`${order.firstName} ${order.lastName}`} />
            <InfoGridItem icon={<Phone size={13} />}      label="Phone"   value={order.phone} />
            <InfoGridItem icon={<GeoAltFill size={13} />} label="Address" value={`${order.address.buildingEstateName}, ${order.address.unitFloor}`} />
            <InfoGridItem icon={<Calendar size={13} />}   label="Date"    value={formatFirebaseDate(order.createdAt)} />
          </div>

          <Divider />

          {/* Order summary */}
          <SectionLabel label="Order" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 24px', marginBottom: 4 }}>
            <InfoGridItem icon={<Box size={13} />}        label="Items"   value={`${getCartItemCount(order.cart)} items`} />
            <InfoGridItem icon={<CreditCard size={13} />} label="Payment" value={order.paymentMethod} />
          </div>

          <Divider />

          {/* Cart items */}
          <SectionLabel label="Cart" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 4 }}>
            {order.cart?.length ? (
              order.cart.map((item) => (
                <div
                  key={`${order.id}-${item.id}-${item.optionItemId || "item"}`}
                  style={{ backgroundColor: '#F2F2F7', borderRadius: 12, padding: '8px 12px' }}
                >
                  <OrderItem {...item} showPrice />
                </div>
              ))
            ) : (
              <span style={{ fontSize: '0.875rem', color: 'rgba(60,60,67,0.38)' }}>
                No cart items available.
              </span>
            )}
          </div>

          <Divider />

          {/* Cost breakdown */}
          <SectionLabel label="Cost Breakdown" />
          <div style={{ backgroundColor: '#F2F2F7', borderRadius: 12, padding: '14px 16px' }}>
            {costLines.map(({ label, value }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 9 }}>
                <span style={{ fontSize: '0.875rem', color: 'rgba(60,60,67,0.52)' }}>{label}</span>
                <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#1c1c1e' }}>{formatSignedCurrency(value)}</span>
              </div>
            ))}
            <div style={{
              borderTop: '1px solid rgba(60,60,67,0.1)', paddingTop: 10, marginTop: 2,
              display: 'flex', justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#1c1c1e' }}>Total</span>
              <span style={{ fontSize: '1rem', fontWeight: 700, color: '#007AFF' }}>{formatCurrency(costBreakdown.total)}</span>
            </div>
          </div>

        </div>
      </Modal.Body>
    </Modal>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const Orders = () => {
  const [statusFilter,  setStatusFilter]  = useState('all');
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [orders,        setOrders]        = useState([]);
  const [orderCount,    setOrderCount]    = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal,     setShowModal]     = useState(false);

  const { isAuthorized, isLoading: authLoading, user, error: authError, logout } = useMerchantAuth();
  const { token } = useUserContext();

  const fetchOrders = async (filter = 'all') => {
    if (!user?.uid || !token) return;
    try {
      setLoading(true);
      setError(null);
      const response = await getMerchantOrderHistory(user.uid, filter, token);
      if (response && response.orderHistory) {
        setOrders(response.orderHistory);
        setOrderCount(response.orderCount || 0);
      } else {
        setOrders([]);
        setOrderCount(0);
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("Failed to fetch orders. Please try again.");
      setOrders([]);
      setOrderCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthorized && user?.uid && token) {
      fetchOrders(statusFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized, user?.uid, token, statusFilter]);

  useEffect(() => {
    if (authError) {
      console.error("Authentication error:", authError);
    }
  }, [authError]);

  const filteredOrders = useMemo(() => {
    if (statusFilter === 'all') return orders;
    return orders.filter(order => order.status === statusFilter);
  }, [orders, statusFilter]);

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedOrder(null);
  };

  // Auth loading — skeleton
  if (authLoading) {
    return (
      <div style={PAGE}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 16px 16px' }}>
          <Skel w="38%" h={26} r={8} style={{ marginBottom: 8 }} />
          <Skel w="22%" h={14} r={6} style={{ marginBottom: 24 }} />
          <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
            {[60, 54, 72, 78, 64, 76, 70].map((w, i) => (
              <Skel key={i} w={w} h={32} r={16} />
            ))}
          </div>
          <SkeletonGrid />
        </div>
      </div>
    );
  }

  // Unauthorized
  if (!isAuthorized) {
    return (
      <div style={PAGE}>
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', minHeight: '80vh', padding: 32,
        }}>
          <div style={{
            width: 60, height: 60, borderRadius: 18,
            backgroundColor: 'rgba(255,149,0,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 16,
          }}>
            <ShieldExclamation size={28} color="#FF9500" />
          </div>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#1c1c1e', marginBottom: 6, textAlign: 'center' }}>
            Access Restricted
          </h3>
          <p style={{ fontSize: '0.875rem', color: 'rgba(60,60,67,0.5)', textAlign: 'center', marginBottom: 24 }}>
            Merchant authentication required.
          </p>
          <button
            onClick={logout}
            style={{
              padding: '11px 28px', borderRadius: 12, border: 'none',
              backgroundColor: '#007AFF', color: '#fff',
              fontSize: '0.9375rem', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Sign In as Merchant
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={PAGE}>
      <Seo {...generateNoIndexSEO("Store Orders")} />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 16px 0' }}>

        {/* Header */}
        <div style={{ marginBottom: 18 }}>
          <h2 style={{ fontSize: '1.375rem', fontWeight: 700, color: '#1c1c1e', letterSpacing: '-0.03em', margin: '0 0 4px' }}>
            Order History
          </h2>
          {(orderCount > 0 || user?.email) && (
            <p style={{ fontSize: '0.8125rem', color: 'rgba(60,60,67,0.48)', margin: 0 }}>
              {orderCount > 0 ? `${orderCount} orders` : ''}
              {orderCount > 0 && user?.email ? ' · ' : ''}
              {user?.email ?? ''}
            </p>
          )}
        </div>

        {/* Filter chips */}
        <div style={{ marginBottom: 20 }}>
          <FilterChips value={statusFilter} onChange={setStatusFilter} />
        </div>

        {/* Error banner */}
        {error && (
          <div style={{
            backgroundColor: 'rgba(255,59,48,0.08)', borderRadius: 12,
            padding: '12px 16px', marginBottom: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: '0.875rem', color: '#FF3B30' }}>{error}</span>
            <button
              onClick={() => fetchOrders(statusFilter)}
              style={{
                background: 'none', border: 'none', padding: 0,
                fontSize: '0.8125rem', color: '#007AFF', fontWeight: 600, cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <SkeletonGrid />
        ) : filteredOrders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '72px 24px' }}
          >
            <div style={{
              width: 56, height: 56, borderRadius: 16,
              backgroundColor: 'rgba(60,60,67,0.06)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 16,
            }}>
              <BoxSeam size={26} color="rgba(60,60,67,0.28)" />
            </div>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, color: '#1c1c1e', marginBottom: 4 }}>No orders found</h4>
            <p style={{ fontSize: '0.875rem', color: 'rgba(60,60,67,0.42)', margin: 0 }}>
              No orders match this filter.
            </p>
          </motion.div>
        ) : (
          <AnimatePresence>
            <Row className="g-3 pb-4">
              {filteredOrders.map((order, idx) => (
                <Col xs={12} sm={6} lg={4} key={order.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.25, delay: Math.min(idx * 0.04, 0.4) }}
                    style={{ height: '100%' }}
                  >
                    <OrderCard order={order} onViewDetails={handleViewDetails} />
                  </motion.div>
                </Col>
              ))}
            </Row>
          </AnimatePresence>
        )}

      </div>

      <OrderDetailsModal show={showModal} onHide={handleCloseModal} order={selectedOrder} />
    </div>
  );
};

export default Orders;
